import React from 'react';
import { Trash2, AlertCircle, Edit3, CheckCircle, XCircle, Loader2 } from 'lucide-react';

const DynamicModal = ({ 
  isOpen, 
  type = 'alert', // 'delete' | 'alert' | 'rename' | 'success' | 'error' | 'custom'
  title, 
  message, 
  file, 
  failedFiles = [], 
  onClose, 
  onConfirm, 
  isLoading = false,
  confirmText,
  cancelText = 'Batal',
  children 
}) => {
  if (!isOpen) return null;

  // Derive default title based on type if not provided
  const getDefaultTitle = () => {
    switch (type) {
      case 'delete': return 'Hapus Data?';
      case 'rename': return 'Beri Nama';
      case 'success': return 'Berhasil';
      case 'error': return 'Terjadi Kesalahan';
      case 'alert': return 'Perhatian';
      default: return 'Informasi';
    }
  };

  const modalTitle = title || getDefaultTitle();

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden transform scale-100 transition-all flex flex-col max-h-[90vh]">
        <div className="p-6 flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
          
          {/* Header Icon */}
          <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-5 mx-auto border-[4px] ${
            (type === 'delete' || type === 'error') ? 'bg-red-50 border-red-50/50' :
            type === 'rename' ? 'bg-blue-50 border-blue-50/50' :
            type === 'success' ? 'bg-emerald-50 border-emerald-50/50' :
            'bg-orange-50 border-orange-50/50'
          }`}>
            {type === 'delete' && <Trash2 size={24} className="text-red-500" />}
            {type === 'alert' && <AlertCircle size={24} className="text-orange-500" />}
            {type === 'rename' && <Edit3 size={24} className="text-blue-500" />}
            {type === 'success' && <CheckCircle size={24} className="text-emerald-500" />}
            {type === 'error' && <XCircle size={24} className="text-red-500" />}
            {type === 'custom' && <AlertCircle size={24} className="text-slate-500" />}
          </div>
          
          <h3 className="text-lg font-bold text-slate-800 text-center mb-2">
            {modalTitle}
          </h3>

          {/* Body Content */}
          {type === 'delete' && (
            <p className="text-sm text-slate-500 text-center mb-6 px-2">
              Apakah Anda yakin ingin menghapus {file ? 'dokumen' : 'data ini'}? <br />
              {file && (
                 <span className="font-semibold text-slate-700 block truncate mt-1" title={file.name}>{file.name}</span>
              )}
              {message ? <span className="block mt-1">{message}</span> : <span className="block mt-1">Data ini tidak dapat dikembalikan.</span>}
            </p>
          )}

          {type === 'alert' && (
            <p className="text-sm text-slate-500 text-center mb-6 px-2">
              {message}
            </p>
          )}

          {type === 'success' && (
            <p className="text-sm text-slate-500 text-center mb-6 px-2">
              {message}
            </p>
          )}

          {type === 'error' && (
            <div className="flex flex-col gap-2 mb-6 mt-2 px-2">
              <p className="text-sm text-slate-500 text-center">{message}</p>
              {failedFiles && failedFiles.length > 0 && (
                <ul className="text-sm text-red-600 font-medium bg-red-50 rounded-xl p-3 border border-red-100 mt-2 flex flex-col gap-2 max-h-40 overflow-y-auto">
                  {failedFiles.map((fileObj, idx) => (
                    <li key={idx} className="flex flex-col border-b border-red-200/50 last:border-0 pb-2 last:pb-0">
                      <span className="truncate font-bold" title={fileObj.name || fileObj}>{fileObj.name || fileObj}</span>
                      {fileObj.reason && (
                        <span className="text-xs text-red-500 mt-0.5 leading-snug">{fileObj.reason}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Custom Children for forms (like rename inputs) */}
          {children}

          {/* Footer Buttons */}
          <div className="flex items-center gap-3 mt-auto">
            {type === 'delete' && (
              <>
                <button
                  onClick={onClose}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-semibold hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {cancelText}
                </button>
                <button
                  onClick={onConfirm}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 text-white font-semibold hover:bg-red-600 transition-colors shadow-lg shadow-red-500/30 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isLoading ? <Loader2 size={18} className="animate-spin" /> : null}
                  {isLoading ? 'Menghapus...' : confirmText || 'Ya, Hapus'}
                </button>
              </>
            )}

            {(type === 'alert' || type === 'success' || type === 'error') && (
              <button
                onClick={onClose}
                className={`w-full px-4 py-2.5 rounded-xl text-white font-semibold shadow-lg transition-colors ${
                  type === 'success' ? 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/30' :
                  type === 'error' ? 'bg-red-600 hover:bg-red-700 shadow-red-600/30' :
                  'bg-slate-800 hover:bg-slate-900 shadow-slate-800/30'
                }`}
              >
                {type === 'error' ? 'Tutup' : 'Mengerti'}
              </button>
            )}

            {type === 'rename' && (
              <>
                <button
                  onClick={onClose}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-semibold hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {cancelText}
                </button>
                <button
                  onClick={onConfirm}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors shadow-lg shadow-blue-600/30 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isLoading ? <Loader2 size={18} className="animate-spin" /> : null}
                  {isLoading ? 'Mengupload...' : confirmText || 'Simpan'}
                </button>
              </>
            )}
            
            {type === 'custom' && (
              <>
                 <button
                  onClick={onClose}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-semibold hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {cancelText}
                </button>
                <button
                  onClick={onConfirm}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-slate-800 text-white font-semibold hover:bg-slate-900 transition-colors shadow-lg shadow-slate-800/30 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isLoading ? <Loader2 size={18} className="animate-spin" /> : null}
                  {confirmText || 'Simpan'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DynamicModal;
